import { Stringifier } from '@dandelion/core';
import sodium from 'libsodium-wrappers';
import RegistryForHash from '../interfaces/Registry';

//TODO: remove when Crypt.ts is exported from core, and generateHash is generalized
export class Crypt {
  static generateKey(): string {
    const key = sodium.crypto_secretbox_keygen();
    return sodium.to_hex(key);
  }

  static async decrypt(encObject: string, myKey: string | undefined): Promise<any> {
    await sodium.ready;
    const key = sodium.from_hex(myKey!);
    const nonce_and_ciphertext = Crypt.u_atob(encObject); //converts ascii string of garbled text into binary
    const nonce = nonce_and_ciphertext.slice(0, sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES); //https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/TypedArray/slice
    const ciphertext = nonce_and_ciphertext.slice(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
    const result = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(nonce, ciphertext, null, nonce, key, 'text');
    return new Stringifier().deserialize(result);
  }

  private static base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  // Helper method to hash a Uint8Array using SHA-256
  private static async sha256(data: Uint8Array): Promise<Uint8Array> {
    const hashBuffer = await crypto.subtle.digest('SHA-256', new Uint8Array(data));
    return new Uint8Array(hashBuffer);
  }

  private static concatTypedArray(ResultConstructor: any, ...arrays: Uint8Array[]) {
    let totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0);
    let result = new ResultConstructor(totalLength);
    let offset = 0;
    for (let arr of arrays) {
      result.set(arr, offset);
      offset += arr.length;
    }
    return result;
  }

  private static u_btoa(buffer: any) {
    //https://stackoverflow.com/a/43271130/
    const binary = [];
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary.push(String.fromCharCode(bytes[i]));
    }
    return btoa(binary.join(''));
  }

  private static u_atob(ascii: string) {
    //https://stackoverflow.com/a/43271130/
    return Uint8Array.from(atob(ascii), (c) => c.charCodeAt(0));
  }

  static async encrypt(clearObject: any, myKey: string): Promise<string> {
    await sodium.ready;
    const keyUint8Array = Crypt.base64ToUint8Array(myKey); // Convert base64 key to Uint8Array
    const key = await Crypt.sha256(keyUint8Array); // Hash the key to get a 32-byte Uint8Array
    if (key.length !== 32) {
      throw new Error('Key hashing failed to produce a 32-byte array');
    }
    const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
    const serializedObject = new TextEncoder().encode(JSON.stringify(clearObject));
    const encrypted = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      serializedObject,
      null,
      null,
      nonce,
      key
    );
    const nonce_and_ciphertext = Crypt.concatTypedArray(Uint8Array, nonce, encrypted);
    return Crypt.u_btoa(nonce_and_ciphertext);
  }

  static async generateHash(variablesToHash: RegistryForHash): Promise<string> {  
    const { name, surname, birthDate, centreCode } = variablesToHash;
     const concatenatedData = `${this.normalizeForSearchOrHash(name ?? '')}${this.normalizeForSearchOrHash(surname ?? '')}${birthDate}${this.normalizeForSearchOrHash(centreCode ?? '')}`;
    await sodium.ready;
    const hash = sodium.crypto_generichash(sodium.crypto_generichash_BYTES, concatenatedData, "");

    return Array.from(hash)
      .map(n => n.toString(16).padStart(2, '0'))
      .join('');
  }

  static normalizeForSearchOrHash(str: string): string {
    return str
      .normalize('NFKD') // Normalize substituting special Unicode chars with standard and splitting up diacritics
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/\s+/g, '') // Remove spaces
      .toLowerCase();
  }
}
